import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchCart, addToCart as apiAddToCart, updateCartItem, removeFromCart as apiRemoveFromCart, clearCart as apiClearCart, fetchProducts } from '../services/api';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load products and cart on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [cartData, productsData] = await Promise.all([
          fetchCart(),
          fetchProducts()
        ]);
        setCart(cartData);
        setProducts(productsData);
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Get product details for cart items
  const getCartItemsWithDetails = useCallback(() => {
    return cart.items.map(item => {
      const product = products.find(p => p.id === item.product_id);
      return {
        ...item,
        product,
        name: product?.name || 'Unknown Product',
        color: product?.color || '',
        price: product?.price || 0,
        image: product?.images?.[0] || '',
      };
    });
  }, [cart.items, products]);

  const addToCart = async (productId, size, quantity = 1) => {
    try {
      await apiAddToCart(productId, size, quantity);
      const updatedCart = await fetchCart();
      setCart(updatedCart);
      return true;
    } catch (err) {
      console.error('Failed to add to cart:', err);
      setError(err.message);
      return false;
    }
  };

  const updateQuantity = async (productId, size, quantity) => {
    try {
      await updateCartItem(productId, size, quantity);
      const updatedCart = await fetchCart();
      setCart(updatedCart);
    } catch (err) {
      console.error('Failed to update quantity:', err);
      setError(err.message);
    }
  };

  const removeItem = async (productId, size) => {
    try {
      await apiRemoveFromCart(productId, size);
      const updatedCart = await fetchCart();
      setCart(updatedCart);
    } catch (err) {
      console.error('Failed to remove item:', err);
      setError(err.message);
    }
  };

  const clearCartItems = async () => {
    try {
      await apiClearCart();
      setCart({ ...cart, items: [] });
    } catch (err) {
      console.error('Failed to clear cart:', err);
      setError(err.message);
    }
  };

  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  const subtotal = getCartItemsWithDetails().reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const value = {
    cart,
    products,
    loading,
    error,
    cartCount,
    subtotal,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart: clearCartItems,
    getCartItemsWithDetails,
    refreshCart: async () => {
      const updatedCart = await fetchCart();
      setCart(updatedCart);
    },
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
